import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { useHistory } from 'react-router';
import FolderOpenIcon from '@material-ui/icons/FolderOpen';
import AssignmentTurnedInIcon from '@material-ui/icons/AssignmentTurnedIn';
import ChecklistIcon from '@material-ui/icons/PlaylistAddCheck';
import ReceiptIcon from '@material-ui/icons/Receipt';
import ConfirmationNumberIcon from '@material-ui/icons/ConfirmationNumber';
import ListAltIcon from '@material-ui/icons/ListAlt';
import PlaceIcon from '@material-ui/icons/Place';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';

import { RootState } from '../../../../../redux/root';
import useProjectsViewItems, { ProjectViewItem } from '../../../hooks/useProjectsViewItems';
import { setShowDrawerContent } from '../../../../../redux/slices/generalSlice';
import { loadDocumentsData } from '../Documents/DocumentsTable';
import { useGetAllPermitInspectionsQuery } from '../../../../../redux/rtkQuery/apiSlice';
import { setSelectedPermit } from '../../../../../redux/slices/inspectionsSlice';
import { setSelectedProjectInvoice } from '../../../../../redux/slices/feesSlice';
import { onConditionSelectedWithDispatch } from '../Conditions/ConditionsTable';
import { loadActivitiesData } from '../Activities/ActivitiesTable';

type TileTone = 'blue' | 'green' | 'amber' | 'purple' | 'teal';

const tonePalette: Record<TileTone, { bg: string; fg: string; border: string }> = {
  blue: { bg: '#E9F2FF', fg: '#2563EB', border: '#CFE6F3' },
  green: { bg: '#DCFCE7', fg: '#16A34A', border: '#C5E7D2' },
  amber: { bg: '#FFF1D6', fg: '#B7800A', border: '#FFE7AC' },
  purple: { bg: '#F3E8FF', fg: '#9333EA', border: '#E9D5FF' },
  teal: { bg: '#CFFAF4', fg: '#0E9488', border: '#A7E8DF' },
};

const iconByLabel: Record<string, { icon: React.ReactNode; tone: TileTone; hint: string }> = {
  Activities: { icon: <ListAltIcon />, tone: 'teal', hint: 'Permitting, proposals and reviews' },
  Documents: { icon: <FolderOpenIcon />, tone: 'blue', hint: 'Submittals, approvals, plans' },
  Inspections: { icon: <AssignmentTurnedInIcon />, tone: 'green', hint: 'Scheduled and completed inspections' },
  Conditions: { icon: <ChecklistIcon />, tone: 'amber', hint: 'Open requirements and sign-offs' },
  Fees: { icon: <ReceiptIcon />, tone: 'purple', hint: 'Invoices and payments' },
  Tickets: { icon: <ConfirmationNumberIcon />, tone: 'teal', hint: 'Permit tickets and labels' },
};

const ProjectDetails = () => {
  const { selectedPermit, linkedItems } = useProjectsViewItems();
  const [permitId, setPermitId] = useState<number>(-1);
  const history = useHistory();
  const dispatch = useDispatch();
  const vocabulary = useSelector((s: RootState) => s.general.vocabulary);
  const show = useSelector((s: RootState) => s.general.showDrawerContent);
  const clientProjecPermitList = useSelector((s: RootState) => s.fees.clientProjecPermitList);
  const { data } = useGetAllPermitInspectionsQuery(selectedPermit.permitId, { skip: permitId === -1 });

  useEffect(() => {
    if (selectedPermit && selectedPermit.permitId) {
      setPermitId(selectedPermit.permitId);
    }
  }, [selectedPermit]);

  useEffect(() => {
    if (data) {
      const _selected = {
        ...selectedPermit,
        inspections: data,
      };
      // @ts-ignore
      dispatch(setSelectedPermit(_selected));
    }
  }, [data, show]);

  const handleClick = (item: ProjectViewItem) => {
    if (item.qty === 0) return;
    if (item.label === 'Inspections') {
      setPermitId(selectedPermit.permitId);
    } else if (item.label === 'Conditions') {
      loadCondition();
    } else if (item.label === 'Fees') {
      clientProjecPermitList.forEach(iil => {
        if (iil.permit.id === selectedPermit.permitId) {
          dispatch(setSelectedProjectInvoice(iil));
        }
      });
    } else if (item.label === 'Activities') {
      loadActivities();
    } else if (item.label === 'Documents') {
      loadDocumentsData(selectedPermit, dispatch, vocabulary);
    }
    dispatch(setShowDrawerContent(true));
    history.push(item.route);
  };

  const loadCondition = async () => {
    await onConditionSelectedWithDispatch(selectedPermit, dispatch);
  };

  const loadActivities = async () => {
    await loadActivitiesData(selectedPermit, dispatch);
  };

  const projectName = [selectedPermit?.name, selectedPermit?.permitName].filter(Boolean).join(' / ');

  return (
    <Root>
      <HeaderCard>
        <HeaderCol>
          <HeaderLabel>Project / Permit</HeaderLabel>
          <HeaderTitle title={projectName}>{projectName || '—'}</HeaderTitle>
        </HeaderCol>
        <HeaderDivider />
        <HeaderCol>
          <HeaderLabel>Address</HeaderLabel>
          <HeaderAddress title={selectedPermit?.address || ''}>
            <PlaceIcon style={{ fontSize: 16 }} />
            <span>{selectedPermit?.address || '—'}</span>
          </HeaderAddress>
        </HeaderCol>
      </HeaderCard>

      <SectionEyebrow>Related items</SectionEyebrow>
      <Tiles>
        {linkedItems.filter(item => !['Tickets', 'Documents'].includes(item.label)).map(item => {
          const meta = iconByLabel[item.label];
          const tone = meta?.tone || 'blue';
          const palette = tonePalette[tone];
          const hasData = item.qty > 0;
          return (
            <Tile key={item.label} $disabled={!hasData}>
              <TileTop>
                <IconBadge style={{ background: palette.bg, color: palette.fg, borderColor: palette.border }}>
                  {meta?.icon}
                </IconBadge>
                <QtyChip $hasData={hasData} style={hasData ? { background: palette.bg, color: palette.fg, borderColor: palette.border } : undefined}>
                  {item.qty}
                </QtyChip>
              </TileTop>
              <TileBody>
                <TileLabel>{item.label}</TileLabel>
                <TileHint>{meta?.hint}</TileHint>
              </TileBody>
              <ViewButton type="button" onClick={() => handleClick(item)} disabled={!hasData}>
                {hasData ? 'View details' : 'Nothing to show'}
                {hasData && <ArrowForwardIcon style={{ fontSize: 16 }} />}
              </ViewButton>
            </Tile>
          );
        })}
      </Tiles>
    </Root>
  );
};

export default ProjectDetails;

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont,
    'Helvetica Neue', Arial, Roboto, sans-serif;
`;

const HeaderCard = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 1px minmax(0, 1fr);
  gap: 24px;
  align-items: stretch;
  padding: 18px 20px;
  background: var(--neutral-50);
  border: 1px solid var(--border);
  border-radius: 10px;

  @media only screen and (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 14px;
  }
`;

const HeaderCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const HeaderDivider = styled.div`
  background: var(--border);
  width: 1px;

  @media only screen and (max-width: 640px) {
    display: none;
  }
`;

const HeaderLabel = styled.span`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--neutral-500);
`;

const HeaderTitle = styled.span`
  font-size: 1.0625rem;
  font-weight: 700;
  color: var(--mainText);
  line-height: 1.35;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const HeaderAddress = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.9375rem;
  color: var(--neutral-700);
  line-height: 1.35;
  min-width: 0;

  svg {
    color: var(--neutral-500);
    flex-shrink: 0;
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const SectionEyebrow = styled.div`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--neutral-500);
  padding: 0 2px;
`;

const Tiles = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 14px;
`;

const Tile = styled.div<{ $disabled: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 16px;
  background: var(--neutral-0);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: var(--shadow-xs);
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
  min-height: 152px;
  opacity: ${({ $disabled }) => ($disabled ? 0.7 : 1)};

  &:hover {
    ${({ $disabled }) =>
      $disabled
        ? ''
        : `
      border-color: var(--neutral-300);
      box-shadow: var(--shadow-sm, 0 2px 6px rgba(15, 23, 42, 0.08));
      transform: translateY(-1px);
    `}
  }
`;

const TileTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const IconBadge = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: 1px solid transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  svg {
    font-size: 22px;
  }
`;

const QtyChip = styled.span<{ $hasData: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 34px;
  height: 26px;
  padding: 0 10px;
  border-radius: 999px;
  border: 1px solid var(--border);
  background: var(--neutral-100);
  color: var(--neutral-500);
  font-weight: 700;
  font-size: 0.8125rem;
  font-variant-numeric: tabular-nums;
`;

const TileBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
`;

const TileLabel = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: var(--mainText);
  line-height: 1.3;
`;

const TileHint = styled.div`
  font-size: 0.8125rem;
  color: var(--neutral-500);
  line-height: 1.4;
`;

const ViewButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 36px;
  padding: 0 12px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--neutral-0);
  color: var(--neutral-700);
  font-family: inherit;
  font-weight: 600;
  font-size: 0.8125rem;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease;

  &:hover:not(:disabled) {
    background: var(--primary-600);
    color: var(--neutral-0);
    border-color: var(--primary-600);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }

  &:disabled {
    color: var(--neutral-400);
    cursor: not-allowed;
    background: var(--neutral-50);
  }
`;
